import { apiFetch } from './api';
import type { FieldTypeKey } from './data-tables-catalog';

export interface ComputationDef {
  kind: 'sum' | 'count' | 'min' | 'max';
  source: string;
  field?: string;
  filter?: Record<string, unknown>;
  description: string;
}

/** Конфіг для type='stat' — admin задає aggregate-агрегацію над пов'язаною таблицею.
 *  Backend обчислює значення на read і інжектить у customFields/values. */
export interface StatConfig {
  aggregator: 'count' | 'sum' | 'avg' | 'min' | 'max';
  target_slug: string;
  link_field: string;
  target_field?: string;
}

export interface ApiField {
  id: string;
  fieldKey: string;
  label: string;
  type: FieldTypeKey;
  required: boolean;
  primary: boolean;
  builtIn: boolean;
  /** Ядрове поле — не можна сховати/видалити, тільки rename/group. */
  protected?: boolean;
  /** Якщо true — поле не показується у PersonDetail (override hide). */
  hidden?: boolean;
  /** Назва групи (null = "без групи"). */
  groupName?: string | null;
  /** Position у межах своєї групи. */
  position?: number;
  /** Для dropdown — список варіантів. */
  options?: string[] | null;
  /** Value обчислюється системою; readonly у UI. */
  is_computed?: boolean;
  /** Опис обчислення для tooltip/badge. */
  computation?: ComputationDef | null;
  /** Чи показувати поле колонкою у list-view. Default true. */
  show_in_list?: boolean;
  /** Для linked_record/linked_summary — slug пов'язаної таблиці. */
  target_slug?: string | null;
  /** Для linked_record — 'one' (single UUID) або 'many' (array). Default 'one'. */
  cardinality?: 'one' | 'many';
  /** Для type='formula' — вираз для обчислення на read. */
  formula_expression?: string | null;
  /** Для type='stat' — aggregate config. Backend обчислює значення на read. */
  stat_config?: StatConfig | null;
  /** Phase 4 per-field permissions: мінімальна роль для перегляду / редагування. */
  min_view_role?: 'READER' | 'ANALYST' | 'ADMIN' | null;
  min_edit_role?: 'READER' | 'ANALYST' | 'ADMIN' | null;
}

export interface ApiFieldGroup {
  id: string;
  name: string;
  position: number;
}

export interface ApiTable {
  slug: string;
  name: string;
  icon: string;
  color: string;
  description: string | null;
  builtIn: boolean;
  fields: ApiField[];
  /** Групи custom-полів у цій таблиці, відсортовані за position. */
  groups?: ApiFieldGroup[];
}

export function fetchDataTables(): Promise<{ items: ApiTable[] }> {
  return apiFetch('/admin/data-tables');
}

/** Публічний (для всіх authed) — для сайдбара та /tables/:slug сторінки. */
export function fetchPublicDataTables(): Promise<{ items: ApiTable[] }> {
  return apiFetch('/data-tables');
}

export function fetchPublicDataTable(slug: string): Promise<ApiTable> {
  return apiFetch(`/data-tables/${slug}`);
}

export interface ApiRecord {
  id: string;
  values: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export function fetchDataRecords(
  slug: string,
  opts: { page?: number; pageSize?: number; sort?: string } = {},
): Promise<{ items: ApiRecord[]; total: number; page: number; pageSize: number }> {
  const p = new URLSearchParams();
  if (opts.page) p.set('page', String(opts.page));
  if (opts.pageSize) p.set('pageSize', String(opts.pageSize));
  if (opts.sort) p.set('sort', opts.sort);
  const qs = p.toString();
  return apiFetch(`/data-tables/${slug}/records${qs ? '?' + qs : ''}`);
}

export function fetchDataRecord(slug: string, id: string): Promise<ApiRecord> {
  return apiFetch(`/data-tables/${slug}/records/${id}`);
}

/** Reverse linked-records: записи з інших таблиць які посилаються на (slug, id). */
export interface ReverseLinkGroup {
  tableSlug: string;
  tableName: string;
  tableIcon: string;
  fieldLabel: string;
  fieldKey: string;
  records: { id: string; label: string }[];
  total: number;
}

export function fetchReverseLinks(
  slug: string,
  id: string,
): Promise<{ groups: ReverseLinkGroup[] }> {
  return apiFetch(`/lookup/reverse/${encodeURIComponent(slug)}/${encodeURIComponent(id)}`);
}

export function createDataRecord(slug: string, values: Record<string, unknown>): Promise<{ id: string }> {
  return apiFetch(`/data-tables/${slug}/records`, { method: 'POST', body: { values } });
}

// =============================================================================
//  Relation cards (bubbles) + fuzzy picker search
// =============================================================================

/** Rich payload для relation-bubble. `label` === `title` (back-compat). */
export interface RelationCard {
  id: string;
  title: string;
  subtitle: string | null;
  email: string | null;
  phone: string | null;
  telegram: string | null;
  type: string;
  initials: string;
  label: string;
  deleted?: boolean;
}

/** Batch-резолв id → RelationCard (один HTTP на пачку; кеш у linked-cache.ts). */
export function resolveRelationCards(slug: string, ids: string[]): Promise<{ items: RelationCard[] }> {
  return apiFetch(`/lookup/${encodeURIComponent(slug)}?ids=${ids.join(',')}`);
}

/** Fuzzy-пошук записів цільової таблиці для relation-picker (pg_trgm / ILIKE). */
export function searchRelation(
  slug: string,
  opts: { q?: string; limit?: number; cursor?: string } = {},
): Promise<{ items: RelationCard[]; nextCursor: string | null }> {
  const p = new URLSearchParams();
  if (opts.q) p.set('q', opts.q);
  if (opts.limit) p.set('limit', String(opts.limit));
  if (opts.cursor) p.set('cursor', opts.cursor);
  const qs = p.toString();
  return apiFetch(`/lookup/${encodeURIComponent(slug)}/search${qs ? '?' + qs : ''}`);
}

export function updateDataRecord(slug: string, id: string, values: Record<string, unknown>): Promise<{ ok: true }> {
  return apiFetch(`/data-tables/${slug}/records/${id}`, { method: 'PATCH', body: { values } });
}

export function deleteDataRecord(slug: string, id: string): Promise<{ ok: true }> {
  return apiFetch(`/data-tables/${slug}/records/${id}`, { method: 'DELETE' });
}

export function bulkImportRecords(
  slug: string,
  rows: Record<string, unknown>[],
): Promise<{ imported: number; total: number; skipped: number }> {
  return apiFetch(`/data-tables/${slug}/records/bulk-import`, { method: 'POST', body: { rows } });
}

export function bulkDeleteRecords(slug: string, ids: string[]): Promise<{ deleted: number }> {
  return apiFetch(`/data-tables/${slug}/records/bulk-delete`, { method: 'POST', body: { ids } });
}

export function fetchAllRecordIds(slug: string): Promise<{ ids: string[] }> {
  return apiFetch(`/data-tables/${slug}/records/ids`);
}

// =============================================================================
//  Dedup rules
// =============================================================================

export interface DedupRule {
  id: string;
  tableSlug: string;
  fieldKeys: string[];
  onCreateBehavior: 'warn' | 'block';
  builtIn: boolean;
  ordinal: number;
}

export function fetchDedupRules(): Promise<{ items: DedupRule[] }> {
  return apiFetch('/admin/dedup-rules');
}

export function createDedupRule(body: {
  tableSlug: string;
  fieldKeys: string[];
  onCreateBehavior?: 'warn' | 'block';
}): Promise<{ id: string }> {
  return apiFetch('/admin/dedup-rules', { method: 'POST', body });
}

export function updateDedupRule(
  id: string,
  body: { fieldKeys?: string[]; onCreateBehavior?: 'warn' | 'block' },
): Promise<{ ok: true }> {
  return apiFetch(`/admin/dedup-rules/${id}`, { method: 'PATCH', body });
}

export function deleteDedupRule(id: string): Promise<{ ok: true }> {
  return apiFetch(`/admin/dedup-rules/${id}`, { method: 'DELETE' });
}

export function createDataTable(body: {
  slug: string;
  name: string;
  icon: string;
  color: string;
  description?: string;
}): Promise<{ slug: string }> {
  return apiFetch('/admin/data-tables', { method: 'POST', body });
}

export function updateDataTable(
  slug: string,
  body: { name?: string; icon?: string; color?: string; description?: string | null },
): Promise<{ ok: true }> {
  return apiFetch(`/admin/data-tables/${slug}`, { method: 'PATCH', body });
}

export function deleteDataTable(slug: string): Promise<{ ok: true }> {
  return apiFetch(`/admin/data-tables/${slug}`, { method: 'DELETE' });
}

export function createDataField(
  slug: string,
  body: {
    label: string;
    type: FieldTypeKey;
    fieldKey?: string;
    required?: boolean;
    groupName?: string | null;
    options?: string[];
    showInList?: boolean;
    targetSlug?: string;
    formulaExpression?: string;
    statConfig?: StatConfig;
    minViewRole?: 'READER' | 'ANALYST' | 'ADMIN';
    minEditRole?: 'READER' | 'ANALYST' | 'ADMIN';
  },
): Promise<{ id: string; fieldKey: string }> {
  return apiFetch(`/admin/data-tables/${slug}/fields`, { method: 'POST', body });
}

export function updateDataField(
  slug: string,
  fieldId: string,
  body: {
    label?: string;
    required?: boolean;
    type?: FieldTypeKey;
    groupName?: string | null;
    position?: number;
    options?: string[] | null;
    showInList?: boolean;
    targetSlug?: string | null;
    formulaExpression?: string | null;
    statConfig?: StatConfig | null;
    minViewRole?: 'READER' | 'ANALYST' | 'ADMIN' | null;
    minEditRole?: 'READER' | 'ANALYST' | 'ADMIN' | null;
  },
): Promise<{ ok: true }> {
  return apiFetch(`/admin/data-tables/${slug}/fields/${fieldId}`, { method: 'PATCH', body });
}

export function deleteDataField(slug: string, fieldId: string): Promise<{ ok: true }> {
  return apiFetch(`/admin/data-tables/${slug}/fields/${fieldId}`, { method: 'DELETE' });
}

/**
 * Bulk reorder fields у одній таблиці — переписує position та опційно
 * groupName. Викликається після drag-and-drop у налаштуваннях.
 */
export function reorderDataFields(
  slug: string,
  items: { id: string; position: number; groupName?: string | null }[],
): Promise<{ ok: true }> {
  return apiFetch(`/admin/data-tables/${slug}/fields/reorder`, {
    method: 'POST',
    body: { items },
  });
}

// ============================================================================
// Custom field groups CRUD
// ============================================================================

export function createDataFieldGroup(slug: string, name: string): Promise<{ id: string }> {
  return apiFetch(`/admin/data-tables/${slug}/groups`, { method: 'POST', body: { name } });
}

export function updateDataFieldGroup(
  slug: string,
  groupId: string,
  body: { name?: string; position?: number },
): Promise<{ ok: true }> {
  return apiFetch(`/admin/data-tables/${slug}/groups/${groupId}`, { method: 'PATCH', body });
}

export function deleteDataFieldGroup(slug: string, groupId: string): Promise<{ ok: true }> {
  return apiFetch(`/admin/data-tables/${slug}/groups/${groupId}`, { method: 'DELETE' });
}

export function reorderDataFieldGroups(
  slug: string,
  items: { id: string; position: number }[],
): Promise<{ ok: true }> {
  return apiFetch(`/admin/data-tables/${slug}/groups/reorder`, {
    method: 'POST',
    body: { items },
  });
}

// ============================================================================
// Phase 4: schema versioning (snapshots of data structure)
// ============================================================================

export interface DataStructureSnapshot {
  id: string;
  tableSlug: string;
  name: string | null;
  createdById: string | null;
  createdAt: string;
  fieldCount: number;
  groupCount: number;
}

export function fetchSnapshots(slug: string): Promise<{ items: DataStructureSnapshot[] }> {
  return apiFetch(`/admin/data-tables/${slug}/snapshots`);
}

export function createSnapshot(slug: string, name?: string): Promise<{ id: string }> {
  return apiFetch(`/admin/data-tables/${slug}/snapshots`, {
    method: 'POST',
    body: { name },
  });
}

export function restoreSnapshot(
  slug: string,
  id: string,
): Promise<{ ok: true; restoredFields: number; restoredGroups: number }> {
  return apiFetch(`/admin/data-tables/${slug}/snapshots/${id}/restore`, { method: 'POST' });
}

export function deleteSnapshot(slug: string, id: string): Promise<{ ok: true }> {
  return apiFetch(`/admin/data-tables/${slug}/snapshots/${id}`, { method: 'DELETE' });
}

/* ── View presets ─────────────────────────────────────────────────────────── */

export interface ApiTableView {
  id: string;
  tableSlug: string;
  name: string;
  columns: string[];
  sortKey: string | null;
  filters: unknown;
  isDefault: boolean;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
}

export function fetchTableViews(slug: string): Promise<{ items: ApiTableView[] }> {
  return apiFetch(`/tables/${slug}/views`);
}

export function createTableView(
  slug: string,
  data: { name: string; columns: string[]; sortKey?: string | null; filters?: unknown; isDefault?: boolean },
): Promise<ApiTableView> {
  return apiFetch(`/admin/data-tables/${slug}/views`, { method: 'POST', body: data });
}

export function updateTableView(
  slug: string,
  id: string,
  data: Partial<{ name: string; columns: string[]; sortKey: string | null; filters: unknown; isDefault: boolean }>,
): Promise<ApiTableView> {
  return apiFetch(`/admin/data-tables/${slug}/views/${id}`, { method: 'PATCH', body: data });
}

export function deleteTableView(slug: string, id: string): Promise<{ ok: true }> {
  return apiFetch(`/admin/data-tables/${slug}/views/${id}`, { method: 'DELETE' });
}

/* ── Export jobs ─────────────────────────────────────────────────────────── */

export interface ApiExportJob {
  id: string;
  tableSlug: string;
  label: string;
  status: 'pending' | 'processing' | 'done' | 'failed';
  recordCount: number | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
}

export function fetchExportJobs(): Promise<{ items: ApiExportJob[] }> {
  return apiFetch('/export-jobs');
}

export function createExportJob(data: {
  tableSlug: string;
  label: string;
  params: Record<string, unknown>;
}): Promise<ApiExportJob> {
  return apiFetch('/export-jobs', { method: 'POST', body: data });
}

export function deleteExportJob(id: string): Promise<{ ok: true }> {
  return apiFetch(`/export-jobs/${id}`, { method: 'DELETE' });
}

export function exportJobDownloadUrl(id: string): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/export-jobs/${id}/download`;
}
